import { useState } from 'react'
import Layout from '../components/layouts/index'
import { Paper, Button } from '../components/modules/ui/index'
import { useQueryGetUserMe } from '../queries/userQueries'
import {
  useQueryGetTickets,
  useQueryGetMyTickets,
  useQueryGetTicketById,
  useMutationCreateTicket,
  useMutationAddComment,
  useMutationUpdateComment,
  useMutationDeleteComment,
  useMutationSolveTicket
} from '../queries/ticketQueries'
import { TicketsList, TicketsListSkeleton } from '../components/modules/views/Tickets/TicketsList'
import { CreateTicketModal } from '../components/modules/views/Tickets/CreateTicketModal'
import { TicketDetailModal } from '../components/modules/views/Tickets/TicketDetailModal'
import AddIcon from '@mui/icons-material/Add'
import { sileo } from 'sileo'

export default function Tickets() {
  const { data: userMe } = useQueryGetUserMe()
  const isAdmin = userMe?.role?.admin === true

  const { data: allTickets = [], isLoading: isLoadingAll } = useQueryGetTickets({
    enabled: isAdmin
  })
  const { data: myTickets = [], isLoading: isLoadingMy } = useQueryGetMyTickets({
    enabled: !isAdmin
  })

  const tickets = isAdmin ? allTickets : myTickets
  const isLoading = isAdmin ? isLoadingAll : isLoadingMy

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedTicketId, setSelectedTicketId] = useState(null)

  const { data: selectedTicket } = useQueryGetTicketById(selectedTicketId, {
    enabled: !!selectedTicketId && showDetailModal
  })

  const createTicketMutation = useMutationCreateTicket()
  const addCommentMutation = useMutationAddComment()
  const updateCommentMutation = useMutationUpdateComment()
  const deleteCommentMutation = useMutationDeleteComment()
  const solveTicketMutation = useMutationSolveTicket()

  const handleCreateTicket = async data => {
    try {
      await sileo.promise(createTicketMutation.mutateAsync(data), {
        loading: { title: 'Creando Ticket' },
        success: {
          title: 'Ticket Creado',
          description: (
            <span className='text-green-500/80 font-medium'>Ticket creado exitosamente 👌</span>
          )
        },
        error: {
          title: 'Error de Creación',
          description: 'Error al crear el ticket 🤯'
        }
      })
    } catch (error) {
      console.error('Error al crear ticket:', error)
      throw error
    }
  }

  const handleOpenDetail = ticketId => {
    setSelectedTicketId(ticketId)
    setShowDetailModal(true)
  }

  const handleCloseDetail = () => {
    setShowDetailModal(false)
    setSelectedTicketId(null)
  }

  const handleAddComment = async data => {
    try {
      await sileo.promise(
        addCommentMutation.mutateAsync({
          ticketId: selectedTicketId,
          data
        }),
        {
          loading: { title: 'Enviando Comentario' },
          success: {
            title: 'Comentario Agregado',
            description: (
              <span className='text-green-500/80 font-medium'>
                Comentario agregado exitosamente 👌
              </span>
            )
          },
          error: {
            title: 'Error al Comentar',
            description: 'Error al agregar comentario 🤯'
          }
        }
      )
    } catch (error) {
      console.error('Error al agregar comentario:', error)
    }
  }

  const handleUpdateComment = async (commentId, data) => {
    try {
      await sileo.promise(
        updateCommentMutation.mutateAsync({
          commentId,
          ticketId: selectedTicketId,
          data
        }),
        {
          loading: { title: 'Actualizando Comentario' },
          success: {
            title: 'Comentario Actualizado',
            description: (
              <span className='text-green-500/80 font-medium'>
                Comentario actualizado exitosamente 👌
              </span>
            )
          },
          error: {
            title: 'Error al Actualizar',
            description: 'Error al actualizar comentario 🤯'
          }
        }
      )
    } catch (error) {
      console.error('Error al actualizar comentario:', error)
    }
  }

  const handleDeleteComment = async commentId => {
    try {
      await sileo.promise(
        deleteCommentMutation.mutateAsync({
          commentId,
          ticketId: selectedTicketId
        }),
        {
          loading: { title: 'Eliminando Comentario' },
          success: {
            title: 'Comentario Eliminado',
            description: (
              <span className='text-green-500/80 font-medium'>
                Comentario eliminado exitosamente 👌
              </span>
            )
          },
          error: {
            title: 'Error al Eliminar',
            description: 'Error al eliminar comentario 🤯'
          }
        }
      )
    } catch (error) {
      console.error('Error al eliminar comentario:', error)
    }
  }

  const handleSolveTicket = async data => {
    try {
      await sileo.promise(
        solveTicketMutation.mutateAsync({
          ticketId: selectedTicketId,
          data
        }),
        {
          loading: { title: 'Resolviendo Ticket' },
          success: {
            title: 'Ticket Resuelto',
            description: (
              <span className='text-green-500/80 font-medium'>Ticket resuelto exitosamente 👌</span>
            )
          },
          error: {
            title: 'Error al Resolver',
            description: 'Error al resolver ticket 🤯'
          }
        }
      )
    } catch (error) {
      console.error('Error al resolver ticket:', error)
    }
  }

  return (
    <Layout>
      <div className='p-4'>
        <Paper>
          <div className='p-6'>
            <div className='flex justify-between items-center mb-6'>
              <h1 className='text-2xl font-bold'>Tickets de Soporte</h1>
              <Button
                onClick={() => setShowCreateModal(true)}
                className='rounded-full bg-accent2 text-white'
                title='NUEVO TICKET'
              >
                <AddIcon className='mr-2' />
                Nuevo Ticket
              </Button>
            </div>

            {isLoading ? (
              <TicketsListSkeleton />
            ) : (
              <TicketsList tickets={tickets} onTicketClick={handleOpenDetail} />
            )}
          </div>
        </Paper>

        <CreateTicketModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateTicket={handleCreateTicket}
          isCreating={createTicketMutation.isPending}
        />

        <TicketDetailModal
          open={showDetailModal}
          onClose={handleCloseDetail}
          ticket={selectedTicket}
          currentUserId={userMe?.id}
          isAdmin={isAdmin}
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
          onSolveTicket={handleSolveTicket}
          isAddingComment={addCommentMutation.isPending}
          isUpdatingComment={updateCommentMutation.isPending}
          isSolvingTicket={solveTicketMutation.isPending}
        />
      </div>
    </Layout>
  )
}
